/**
 * Shared helpers for rendering a member's role-scoped custom attributes
 * (e.g. an RS's Geozone / Platinum Geozone), used by both the member profile
 * dialog and the edit-member dialog so the two surfaces stay in step.
 */

/**
 * Append the geo-zone NUMBER to each option label so the RS zone pickers read
 * e.g. "Denver (470)" instead of just "Denver" (WPS-160 note 2 - operators
 * assign by number). Display-only: the stored value stays the bare zone code.
 * Only touches geo-zone-backed fields (`configuration_data.source ===
 * "geo_zone"`); the "All zones" wildcard (value "ALL") is left un-numbered.
 */
export function decorateGeoZoneOptions(
  configurationData: Record<string, unknown> | null | undefined,
): Record<string, unknown> | undefined {
  if (!configurationData || configurationData.source !== 'geo_zone') {
    return configurationData ?? undefined
  }
  const options = configurationData.options
  if (!Array.isArray(options)) return configurationData
  return {
    ...configurationData,
    options: options.map((option) => {
      if (
        !option ||
        typeof option !== 'object' ||
        Array.isArray(option) ||
        !('value' in option) ||
        !('label' in option)
      ) {
        return option
      }
      const value = String((option as { value: unknown }).value ?? '')
      const label = String((option as { label: unknown }).label ?? '')
      if (!value || value === 'ALL' || label.includes(`(${value})`)) {
        return option
      }
      return { ...option, label: `${label} (${value})` }
    }),
  }
}

/**
 * Pull the backend's per-field validation errors out of an API error envelope
 * (`{ status, message, result: { code_name: [msg] } }`) so a conflict such as
 * the one-RS-per-zone guard (WPS-160 #3) renders inline on the offending field
 * instead of only in a toast.
 */
export function fieldErrorsFromApiError(err: unknown): Record<string, string> {
  const result = (
    err as { response?: { data?: { result?: unknown } } } | undefined
  )?.response?.data?.result
  if (!result || typeof result !== 'object' || Array.isArray(result)) return {}
  const out: Record<string, string> = {}
  for (const [code, messages] of Object.entries(result as Record<string, unknown>)) {
    if (Array.isArray(messages)) {
      const text = messages.filter((m) => typeof m === 'string').join(' ')
      if (text) out[code] = text
    } else if (typeof messages === 'string' && messages) {
      out[code] = messages
    }
  }
  return out
}
